/**
 * Client-side offline-access contract. The server signs each permission
 * snapshot with the workspace window (`offlineAccessTtlMs`); these constants
 * mirror the server bounds and drive renewal and warning timing.
 */
const HOUR_MS = 60 * 60 * 1000;

export const OFFLINE_ACCESS_TTL_DEFAULT_HOURS = 72;
export const OFFLINE_ACCESS_TTL_MIN_HOURS = 1;
export const OFFLINE_ACCESS_TTL_MAX_HOURS = 28 * 24;

export const OFFLINE_ACCESS_DEFAULT_TTL_MS =
  OFFLINE_ACCESS_TTL_DEFAULT_HOURS * HOUR_MS;

/**
 * Online clients renew signed grants once this share of their window has
 * elapsed, so going offline always starts with (nearly) the full window.
 */
export const OFFLINE_ACCESS_RENEWAL_FRACTION = 0.1;
export const OFFLINE_ACCESS_MIN_RENEWAL_INTERVAL_MS = 5 * 60 * 1000;

/** Warn (in-app + system notification) after 2/3 of the window offline. */
export const OFFLINE_ACCESS_WARNING_FRACTION = 2 / 3;

export function isValidOfflineAccessTtlMs(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= OFFLINE_ACCESS_TTL_MIN_HOURS * HOUR_MS &&
    value <= OFFLINE_ACCESS_TTL_MAX_HOURS * HOUR_MS
  );
}

export function offlineAccessRenewalDelayMs(windowMs: number): number {
  return Math.max(
    OFFLINE_ACCESS_MIN_RENEWAL_INTERVAL_MS,
    windowMs * OFFLINE_ACCESS_RENEWAL_FRACTION
  );
}

export function offlineAccessWarningLeadMs(ttlMs: number): number {
  return Math.round(ttlMs * (1 - OFFLINE_ACCESS_WARNING_FRACTION));
}
